/**
 * How a bulk AI-enrich row turned out — the classification behind the run's
 * "N enriched" count and its failed-refs panel.
 *
 * A call that completes is NOT an enrichment: the callable happily returns an
 * empty `suggestions` map (the model had nothing to go on, or every answer was
 * strict-dropped as an off-list enum), and counting those as "enriched" told
 * the curator 8 of 8 worked when two rows were all "—". A row is enriched only
 * when it carries at least one proposal the review grid can show.
 */

/** Why a row did not enrich. */
export type EnrichFailureKind =
  /** The call worked but proposed nothing usable. Worth a fresh retry. */
  | 'empty'
  /** A transient failure (timeout, overload, a malformed model reply). Retry. */
  | 'error'
  /** Out of credit, an auth/config problem, a rejected request — retrying the
   *  same call fails the same way until the root cause is fixed. */
  | 'blocked';

export const FAILURE_KIND_LABEL: Record<EnrichFailureKind, string> = {
  empty: 'AI returned no data',
  error: 'Call failed — retryable',
  blocked: 'Call failed — fix the cause before retrying',
};

function isPresent(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

/**
 * Proposals the review grid can actually show: a non-empty value under a key
 * the grid has a column for. `isKnownField` is the grid's own column test, so
 * this count and the rendered row cannot disagree.
 */
export function usableProposalCount(
  suggestions: Record<string, unknown> | null | undefined,
  isKnownField: (key: string) => boolean,
): number {
  if (!suggestions) return 0;
  let n = 0;
  for (const [key, value] of Object.entries(suggestions)) {
    if (isKnownField(key) && isPresent(value)) n += 1;
  }
  return n;
}

/** Firebase callable error codes that mean "this request can never succeed as sent". */
const BLOCKED_CODES = new Set([
  'unauthenticated',
  'permission-denied',
  'failed-precondition',
  'invalid-argument',
]);

/** Substrings of an Anthropic error that a retry cannot fix. */
const BLOCKED_MESSAGES = [
  'credit balance', // "Your credit balance is too low to access the Anthropic API"
  'invalid x-api-key',
  'authentication_error',
  'permission_error',
  'api_key secret is not configured',
];

/**
 * Sort a thrown enrich failure into retryable vs blocked, with the message to
 * show. Unknown failures default to retryable: offering a retry that fails
 * again costs one call, while hiding a retry that would have worked costs the
 * curator the row.
 */
export function classifyEnrichError(err: unknown): { kind: Exclude<EnrichFailureKind, 'empty'>; message: string } {
  const message = err instanceof Error ? err.message : typeof err === 'string' ? err : String(err);
  const rawCode = err && typeof err === 'object' ? (err as { code?: unknown }).code : undefined;
  // The JS SDK prefixes callable codes: 'functions/permission-denied'.
  const code = typeof rawCode === 'string' ? rawCode.replace(/^functions\//, '') : '';
  const lower = message.toLowerCase();
  if (BLOCKED_CODES.has(code) || BLOCKED_MESSAGES.some((m) => lower.includes(m))) {
    return { kind: 'blocked', message: lower.includes('credit balance') ? `Out of Anthropic credit — ${message}` : message };
  }
  return { kind: 'error', message };
}

/** Run tallies, derived from the rows so they stay true after per-row retries. */
export interface EnrichTally {
  enriched: number;
  empty: number;
  error: number;
  blocked: number;
  /**
   * Variants with nothing to change: the variant prompt returns only what
   * differs from the parent, so an empty answer on a variant whose parent
   * already covers it is a result, not a failure.
   */
  matchesParent: number;
}

export function tallyOutcomes(
  rows: Array<{ status: string; failureKind?: EnrichFailureKind; matchesParent?: boolean }>,
): EnrichTally {
  const t: EnrichTally = { enriched: 0, empty: 0, error: 0, blocked: 0, matchesParent: 0 };
  for (const r of rows) {
    if (r.status === 'done' && r.matchesParent) t.matchesParent += 1;
    else if (r.status === 'done') t.enriched += 1;
    else if (r.status === 'failed') t[r.failureKind ?? 'error'] += 1;
  }
  return t;
}

/** "6 of 8 enriched · 2 returned no data · 1 error" — the completion line. */
export function describeTally(t: EnrichTally, total: number): string {
  const parts = [`${t.enriched} of ${total} enriched`];
  if (t.empty > 0) parts.push(`${t.empty} returned no data`);
  if (t.error > 0) parts.push(`${t.error} failed (retryable)`);
  if (t.blocked > 0) parts.push(`${t.blocked} blocked`);
  if (t.matchesParent > 0) {
    parts.push(`${t.matchesParent} variant${t.matchesParent === 1 ? '' : 's'} already match${t.matchesParent === 1 ? 'es' : ''} the parent`);
  }
  return parts.join(' · ');
}
